// Directory access.
//
// The client is given a list of URLs and picks a RANDOM one to start from. The
// randomness is not decoration: with a fixed order the backup URLs would be used
// only during an outage, which in practice means never tested — and an untested
// failover tends not to work precisely when it is needed. The list holds one
// entry today; the mechanism is the point.

import { directoryLookupHeaders } from '$lib/crypto/signer';

import { ClientError } from './errors';
import { joinPath, send } from './http';

function fromRandomStart<T>(items: readonly T[]): T[] {
  const start = Math.floor(Math.random() * items.length);
  return [...items.slice(start), ...items.slice(0, start)];
}

/** Fetches the manifest, trying URLs in turn from a random starting point.
 *  Returns the raw response together with the domain it came from — signature
 *  verification belongs to the layer above, which knows the last accepted
 *  version. */
export async function fetchManifest(
  urls: readonly string[],
): Promise<{ manifest: Record<string, unknown>; domain: string }> {
  if (urls.length === 0) {
    throw new ClientError('directory_not_configured');
  }
  let last: ClientError = new ClientError('service_unavailable');
  for (const url of fromRandomStart(urls)) {
    try {
      const result = await send({ method: 'GET', url: joinPath(url, '/v1/manifest') });
      return { manifest: result.body, domain: new URL(url).host };
    } catch (error) {
      // A hijacked or dead domain can only deny service — it cannot lie, because
      // the content is signed. A failure is a reason to try the next one.
      last = error instanceof ClientError ? error : new ClientError('service_unavailable');
    }
  }
  throw last;
}

/** Lookup of a single entry by its DERIVED key — never by the phone number; a
 *  directory that accepted numbers here would be one configuration change away
 *  from logging them.
 *
 *  This is THE resolution mode: bulk snapshot files are gone, and the entry gate in
 *  front of these files requires a device signature and counts every ask — misses
 *  included — against a per-device and a global budget. The two headers are built
 *  fresh per attempt (each carries a one-time nonce), over the exact pathname the
 *  gate will see. Returns the raw response for proof verification, or null on 404. */
export async function fetchEntryByKey(
  urls: readonly string[],
  keyB64: string,
): Promise<Record<string, unknown> | null> {
  if (urls.length === 0) {
    throw new ClientError('directory_not_configured');
  }
  let last: ClientError = new ClientError('service_unavailable');
  for (const url of fromRandomStart(urls)) {
    try {
      const target = joinPath(url, `/v1/entry/${encodeURIComponent(keyB64)}`);
      const result = await send({
        method: 'GET',
        url: target,
        headers: await directoryLookupHeaders(target.pathname),
      });
      return result.body;
    } catch (error) {
      if (error instanceof ClientError && error.code === 'not_found') {
        return null;
      }
      last = error instanceof ClientError ? error : new ClientError('service_unavailable');
    }
  }
  throw last;
}
